import { DATE_RANGE_OPTIONS, getDateRangeForPreset } from "../../utils/dateRangePresets";
import { useEffect, useRef, useState } from "react";
import classes from "./DateRangeFilter.module.css";

const CalendarIcon = () => (
	<svg aria-hidden="true" className={classes.calendarIcon} fill="none" height="16" viewBox="0 0 24 24" width="16">
		<path
			d="M8 2v3M16 2v3M3.5 9h17M5 5h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
			stroke="#64748B"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.6"
		/>
	</svg>
);

const DateRangeFilter = ({ customEnd, customStart, onChange, presetKey }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [draftEnd, setDraftEnd] = useState(customEnd || "");
	const [draftStart, setDraftStart] = useState(customStart || "");
	const containerRef = useRef(null);
	const endInputRef = useRef(null);
	const startInputRef = useRef(null);

	const activeOption =
		DATE_RANGE_OPTIONS.find((option) => option.key === presetKey) ||
		DATE_RANGE_OPTIONS.find((option) => option.key === "thisMonth");
	const isCustom = presetKey === "custom";

	useEffect(() => {
		setDraftEnd(customEnd || "");
		setDraftStart(customStart || "");
	}, [customEnd, customStart]);

	useEffect(() => {
		const handleOutsideClick = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const emitCustomRange = (start, end) => {
		if (!start || !end) {
			onChange({
				customEnd: end || null,
				customStart: start || null,
				presetKey: "custom",
				range: getDateRangeForPreset("custom", start || end, end || start),
			});
			return;
		}
		if (new Date(start) > new Date(end)) return;
		onChange({
			customEnd: end,
			customStart: start,
			presetKey: "custom",
			range: getDateRangeForPreset("custom", start, end),
		});
	};

	const handleSelect = (key) => {
		setIsOpen(false);
		if (key === "custom") {
			onChange({
				customEnd: draftEnd || null,
				customStart: draftStart || null,
				presetKey: "custom",
				range: getDateRangeForPreset("custom", draftStart || undefined, draftEnd || undefined),
			});
			return;
		}
		setDraftEnd("");
		setDraftStart("");
		onChange({
			customEnd: null,
			customStart: null,
			presetKey: key,
			range: getDateRangeForPreset(key),
		});
	};

	const handleStartChange = (value) => {
		setDraftStart(value);
		emitCustomRange(value, draftEnd);
	};

	const handleEndChange = (value) => {
		setDraftEnd(value);
		emitCustomRange(draftStart, value);
	};

	return (
		<div className={classes.wrap} ref={containerRef}>
			<div className={classes.controls}>
				<div className={classes.dropdown}>
					<button className={classes.trigger} onClick={() => setIsOpen((open) => !open)} type="button">
						<span>{activeOption?.label || "This Month"}</span>
						<svg fill="none" height="16" viewBox="0 0 16 16" width="16">
							<path
								d="M4 6l4 4 4-4"
								stroke="#0B1F2A"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.5"
							/>
						</svg>
					</button>

					{isOpen ? (
						<div className={classes.menu}>
							{DATE_RANGE_OPTIONS.map((option) => (
								<button
									className={
										option.key === presetKey ? `${classes.item} ${classes.itemActive}` : classes.item
									}
									key={option.key}
									onClick={() => handleSelect(option.key)}
									type="button">
									{option.label}
								</button>
							))}
						</div>
					) : null}
				</div>

				{isCustom ? (
					<div className={classes.rangeBox}>
						<button
							className={classes.datePart}
							onClick={() => startInputRef.current?.showPicker?.() || startInputRef.current?.focus()}
							type="button">
							<span className={draftStart ? classes.dateValue : classes.datePlaceholder}>
								{draftStart
									? new Date(`${draftStart}T00:00:00`).toLocaleDateString("en-US", {
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
										})
									: "mm/dd/yyyy"}
							</span>
							<CalendarIcon />
							<input
								className={classes.hiddenDate}
								onChange={(event) => handleStartChange(event.target.value)}
								ref={startInputRef}
								type="date"
								value={draftStart}
							/>
						</button>

						<span className={classes.dash}>-</span>

						<button
							className={classes.datePart}
							onClick={() => endInputRef.current?.showPicker?.() || endInputRef.current?.focus()}
							type="button">
							<span className={draftEnd ? classes.dateValue : classes.datePlaceholder}>
								{draftEnd
									? new Date(`${draftEnd}T00:00:00`).toLocaleDateString("en-US", {
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
										})
									: "mm/dd/yyyy"}
							</span>
							<CalendarIcon />
							<input
								className={classes.hiddenDate}
								onChange={(event) => handleEndChange(event.target.value)}
								ref={endInputRef}
								type="date"
								value={draftEnd}
							/>
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default DateRangeFilter;
